import useFlags from 'launchdarkly-react-client-sdk/lib/useFlags';
import { SyntheticEvent, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AllValidTherapeuticArea } from 'basics/enums/biogen.enums';
import { CiStatus } from 'basics/enums/maya.enums';
import useGetStrategicImperativeByIdQuery from 'basics/graphql/queries/getStrategicImperativeById';
import { SelectOption } from 'basics/options/options.types';
import useStrategicImperativeOptions from 'basics/options/strategicImperative.options';
import { ciToBE } from 'basics/transformers/CustomerInitiative.transformer';
import { ProductsOptionType } from 'basics/types/products.types';
import useCustomerInitiativeFormConfig from 'components/CustomerInitiativeForm/CustomerInitiativeForm.config';
import {
  CiFormCanceltCallbackType,
  CiFormSubmitCallbackType,
} from 'components/CustomerInitiativeForm/CustomerInitiativeForm.types';
import { ProductsContext } from 'contexts/products';
import { useCurrentUserState } from 'contexts/user';
import { CustomerInitiative, StrategicImperative } from 'generated/maya.types';
import { ButtonType } from 'yoda-ui/components/Button/Button.types';
import { useYodaCreateForm } from 'yoda-ui/yodaForm';

const useCustomerInitiativeCreate = (
  parentStrategicImperative: StrategicImperative | undefined,
  customerInitiative: CustomerInitiative | undefined,
  submitCallback: CiFormSubmitCallbackType,
  cancelCallback: CiFormCanceltCallbackType,
) => {
  const { t } = useTranslation();
  const flags = useFlags();

  const { getValues, providerFields, resetForm, useWatchForm, setValue, useWatchField, setFieldShowError } = useYodaCreateForm();
  const { isValid } = useWatchForm();
  const { user } = useCurrentUserState();

  const [loading, setLoading] = useState(true);
  const [siLoading, setSiLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedSi, setselectedSi] = useState<string | null>(null);
  const [selectedTA, setSelectedTA] = useState<string | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const { allProducts } = useContext(ProductsContext);

  const { options: baseStrategicImperativesOptions, loading: strategicImperativesLoading } = useStrategicImperativeOptions(
    {
      _id: parentStrategicImperative?._id,
      year: customerInitiative?.status === CiStatus.ACTIVE ? null : selectedStartDate?.getFullYear() || new Date().getFullYear(),
      ...(user?.country === 'CA' && flags.mayaAkma846EnableDisplaySiForSpecificCountry ? { country: [user.country] } : {}),
    },
  );

  const strategicImperativesOptions = useMemo(() => {
    const currentSi = customerInitiative?.strategicImperative;
    if (currentSi?._id && currentSi?.title) {
      const isOptionMissing = !baseStrategicImperativesOptions.some((option) => option.id === currentSi._id);
      if (isOptionMissing) {
        return [...baseStrategicImperativesOptions, { id: currentSi._id, label: currentSi.title }];
      }
    }

    return baseStrategicImperativesOptions;
  }, [baseStrategicImperativesOptions, customerInitiative?.strategicImperative]);

  const { customerInitiativeFormConfig } = useCustomerInitiativeFormConfig(
    customerInitiative,
    parentStrategicImperative,
    selectedTA,
    selectedStartDate,
    user,
    strategicImperativesOptions,
    strategicImperativesLoading,
  );

  if (customerInitiative) {
    const fieldsName = Object.keys(customerInitiativeFormConfig);
    setFieldShowError(true, fieldsName);
  }

  useEffect(() => {
    if (customerInitiativeFormConfig?.strategicImperative?.options.length) {
      setLoading(false);
    }
  }, [customerInitiativeFormConfig?.strategicImperative?.options, setLoading]);

  const currentStrategicImperative = useGetStrategicImperativeByIdQuery(selectedSi).data;
  useEffect(() => {
    if (currentStrategicImperative) {
      let currentTA = currentStrategicImperative?.therapeuticArea;

      if (currentTA === AllValidTherapeuticArea.BIOSIMS) {
        currentTA = AllValidTherapeuticArea.BBU;
      }

      setValue(customerInitiativeFormConfig.therapeuticArea.name, currentTA);
      setSiLoading(false);
    }
  }, [currentStrategicImperative, customerInitiativeFormConfig.therapeuticArea.name, setValue]);

  const therapeuticAreaKey = useWatchField(customerInitiativeFormConfig.therapeuticArea.name)?.value;
  useEffect(() => {
    setSelectedTA(therapeuticAreaKey);
  }, [therapeuticAreaKey]);

  const date = useWatchField(customerInitiativeFormConfig.date.name)?.value;
  useEffect(() => {
    setSelectedStartDate(date ? date[0] : null);
  }, [date]);

  const strategicImperativeFieldValue = useWatchField(customerInitiativeFormConfig.strategicImperative.name)?.value;

  useEffect(() => {
    if (strategicImperativesOptions.length && strategicImperativeFieldValue) {
      const currentSelectedOption = strategicImperativesOptions?.find((option) => option.id === strategicImperativeFieldValue);

      if (!currentSelectedOption) {
        setValue(customerInitiativeFormConfig.strategicImperative.name, '');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStartDate, strategicImperativesOptions]);

  const handleChangeStrategicImperative = useCallback((event: SyntheticEvent<Element, Event>, value: SelectOption) => {
    setSiLoading(!!value);
    setselectedSi(value?.id as string);
  }, [setselectedSi]);

  const handleChangeTA = useCallback(() => {
    setValue(customerInitiativeFormConfig.product.name, '');
  }, [setValue, customerInitiativeFormConfig]);

  const handleChangeProduct = useCallback((event: SyntheticEvent<Element, Event>, selectedProduct: ProductsOptionType) => {
    if (selectedProduct) {
      const therapeuticAreaValue = selectedProduct.therapeuticArea;
      setValue(customerInitiativeFormConfig.therapeuticArea.name, therapeuticAreaValue);
    }
  }, [setValue, customerInitiativeFormConfig]);

  const handleCancelCustomerInitiativeForm = () => {
    resetForm();
    cancelCallback();
  };

  const handleSaveCustomerInitiativeForm = async () => {
    setIsSubmitting(true);
    const formValues = getValues();
    const formattedCI = ciToBE(formValues);

    await submitCallback(formattedCI);

    setIsSubmitting(false);
  };

  const cancelButtonConfig = {
    buttonProps: {
      onClick: handleCancelCustomerInitiativeForm,
      buttonType: ButtonType.secondary,
    },
    label: t('actions_cancel_label'),
  };

  const submitButtonConfig = {
    buttonProps: {
      onClick: handleSaveCustomerInitiativeForm,
      buttonType: ButtonType.primary,
      disabled: customerInitiative?.status === CiStatus.ACTIVE || !isValid || isSubmitting,
    },
    label: t('actions_save_label'),
    loading: isSubmitting,
  };

  return {
    cancelButtonConfig,
    customerInitiativeFormConfig,
    handleChangeProduct,
    handleChangeStrategicImperative,
    handleChangeTA,
    loading,
    providerFields,
    siLoading,
    submitButtonConfig,
    allProducts,
  };
};

export default useCustomerInitiativeCreate;
